import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function Doc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="ETFs API" description="Explore ETF holdings with the ETFs API. Access fund information, sector weights, geographic exposure, holdings, and fund-of-funds data."  canonicalUrl="https://axionquant.com/docs/01-market-data/ETFs/00-holdings" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">ETFs API</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
          The ETFs API provides comprehensive data on Exchange-Traded Funds including fund information, sector weights, geographic exposure, holdings, and fund-of-funds exposure analysis. This data is essential for ETF research, portfolio construction, and investment analysis.
        </p>
      </div>

      <div className="space-y-12" ref={sectionRef}>
        {/* Get Top Holdings */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 lg:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /etfs/:ticker/holdings
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves the current top holdings of an ETF with detailed position information including shares held, market value, and percentage weights. Critical for analyzing concentration risk and understanding the ETF's underlying exposures.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        ETF ticker symbol (e.g., "SPY", "QQQ", "VTI")
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/etfs/QQQ/holdings', '/etfs/SPY/holdings']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ticker <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Holding company ticker</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">name <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Holding company name</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">weight <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Formatted weight percentage</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">shares <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Number of shares held</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">marketValue <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Total market value in USD</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">rawWeight <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Raw weight as decimal (0.0-1.0)</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Top Holdings" contentRef={sectionRef} />

              <h3 className="font-semibold mb-3">Top Holdings</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="etfs/holdings"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/ETFs/holdings"></Code>
              </div>
            </div>
          </div>
        </div>

        {/* Get All Holdings */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 lg:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /etfs/:ticker/holdings/all
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves the complete list of all holdings for an ETF with weight percentages and the last updated date. Unlike the top holdings endpoint, this provides the full portfolio composition including smaller positions.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        ETF ticker symbol (e.g., "SPY", "QQQ", "VTI")
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/etfs/QQQ/holdings/all', '/etfs/SPY/holdings/all']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">name <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Holding company name</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ticker <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Holding company ticker</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">weight <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Formatted weight percentage</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">rawWeight <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Raw weight as decimal (0.0-1.0)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">since <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Date the holding data was last updated</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="All Holdings" contentRef={sectionRef} />

              <h3 className="font-semibold mb-3">All Holdings</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="etfs/holdings-all"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/ETFs/holdings-all"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
